// Considered: ArcStageListMolecule — rejected because it renders the stage list as its own
// structure; this row's contract says the stages are one `hint` line beside the name
// (`type-composer.jsx:620-623`). The stage list is the right component when a stage is a
// target; here it is provenance. See spec Design System Reuse Audit (`ArcRowMolecule`).
import React from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import TagAtom from '../../atoms/Tag/TagAtom';
import './ArcRowMolecule.scss';

export interface ArcRowMoleculeProps {
  /** The arc template this row names. Shown as its display name when one is known. */
  templateSystemName: string;
  displayName?: string;
  /** The template's stages, in order. Empty when the template defines none — which is said. */
  stages?: string[];
  /**
   * The arc that answers for a sphere when several are running. Exactly one row
   * in a list may carry it, which is why this is a radio and not a checkbox.
   */
  isPrimary?: boolean;
  /**
   * Whether the template's own applicability gate admits spheres of this type
   * (`validateApplicability`). A discovery answer, not the gate: naming an arc
   * that does not fit is allowed, it simply never deploys.
   */
  fits?: boolean;
  /** What the template's gate does admit — shown on the "won't deploy here" chip. */
  appliesTo?: string;
  /** `edit` renders the affordances; `disabled`/`locked` render the same row inert. */
  mode?: 'edit' | 'disabled' | 'locked';
  onMakePrimary?: () => void;
  onRemove?: () => void;
  className?: string;
  'data-testid'?: string;
}

/**
 * One declared default arc (`type-composer.jsx:608-634`).
 *
 * The row exists to answer three questions at a glance that a bare template name
 * cannot: which arc ANSWERS for the sphere when several run (the radio), what the
 * arc actually does (its stages), and whether it will deploy onto this type at all
 * (the applicability chip). The last one is the reason the row is not a text field:
 * a type may name an arc whose own gate excludes it, and nothing at runtime says so.
 */
const ArcRowMolecule: React.FC<ArcRowMoleculeProps> = ({
  templateSystemName,
  displayName,
  stages = [],
  isPrimary = false,
  fits = true,
  appliesTo,
  mode = 'edit',
  onMakePrimary,
  onRemove,
  className = '',
  'data-testid': testId,
}) => {
  const { t } = useTranslation();
  const editable = mode === 'edit';
  const name = displayName || templateSystemName;

  return (
    <div
      className={`arc-row-molecule ${className}`.trim()}
      data-container="each(arcs)"
      data-kind="field"
      data-layout="inline"
      data-muted={fits ? undefined : '1'}
      data-testid={testId}
    >
      <button
        type="button"
        role="radio"
        aria-checked={isPrimary}
        className="arc-row-molecule__radio"
        data-datum="arcs.the-arc-that-answers-for-a-sphere-when-s"
        data-kind="radio-indicator"
        data-on={isPrimary ? '1' : undefined}
        title={t('studio.typeEditor.arcs.primaryExplained')}
        aria-label={t('studio.typeEditor.arcs.primaryExplained')}
        disabled={!editable}
        onClick={editable ? onMakePrimary : undefined}
      />
      <span className="arc-row-molecule__name" data-datum="arcs.a-name" data-kind="label">
        {name}
      </span>
      <span className="arc-row-molecule__stages" data-datum="arcs.no-stages-defined-yet" data-kind="hint">
        {stages.length ? stages.join(' → ') : t('studio.typeEditor.arcs.noStages')}
      </span>
      {isPrimary && (
        <TagAtom
          size="small"
          tone="info"
          label={t('studio.typeEditor.arcs.primaryChip')}
          className="arc-row-molecule__primary"
          data-datum="arcs.primary"
          data-kind="chip"
        />
      )}
      {!fits && (
        <TagAtom
          size="small"
          tone="warn"
          label={t('studio.typeEditor.arcs.wontDeployHere')}
          ariaLabel={t('studio.typeEditor.arcs.appliesToAria', { says: appliesTo ?? '' })}
          className="arc-row-molecule__misfit"
          data-datum="arcs.won-t-deploy-here"
          data-kind="chip"
        />
      )}
      {editable && onRemove && (
        <ButtonAtom
          variant="text"
          size="small"
          icon="pi pi-times"
          aria-label={t('studio.typeEditor.arcs.removeAria', { name })}
          className="arc-row-molecule__remove"
          data-datum="arcs.button"
          data-kind="button"
          onClick={onRemove}
          data-testid={testId ? `${testId}-remove` : undefined}
        />
      )}
    </div>
  );
};

ArcRowMolecule.displayName = 'ArcRowMolecule';

export default ArcRowMolecule;
